/**************************************************************************************************************************
 Copyright (c) 2024 WorkEarly Results LLP

     Name: public.layout.tsx
   Author: Harikrishnan Gangadharan
 Comments: 

/**************************************************************************************************************************
 IMPORTS
***************************************************************************************************************************/
import { useState, type FunctionComponent } from 'react';
import { Outlet } from 'react-router-dom';
import { motion } from 'framer-motion';
import { HiChevronDown } from 'react-icons/hi';
import { useTranslation } from 'react-i18next';

import Logo from '@/shared/logo';
import Statics from '@/constants/statics';
/**************************************************************************************************************************
 IMPLEMENTATIONS
***************************************************************************************************************************/
const Public: FunctionComponent = () => {
  const { t } = useTranslation('common');

  const [openMenu, setOpenMenu] = useState<boolean>(false);

  const toggleMenu = () => setOpenMenu(!openMenu);

  return (
    <div className="w-full h-[100vh] bg-gray-100">
      <motion.header
        className="fixed left-0 w-full shadow-sm drop-shadow-sm z-1000"
        initial={{ top: '-40%' }}
        animate={{ top: 0 }}
        exit={{ top: '-40%' }}
        transition={{
          ease: 'easeInOut',
          duration: 1
        }}
      >
        <nav
          className={`relative max-w-screen-xl mx-auto w-full min-h-[60px] bg-themeBlue-500 flex items-center justify-center px-5 py-2 shadow-md drop-shadow-md ${
            !openMenu
              ? 'rounded-br-[20px] rounded-bl-[20px]'
              : 'lg:rounded-br-[20px] lg:rounded-bl-[20px]'
          }`}
        >
          <div className="flex items-center justify-start gap-3"></div>
          <Logo />
          <button
            className={`lg:hidden absolute bottom-[-15px] left-[88%] right-[12%] h-[30px] w-[30px] bg-themePurple-900 text-white shadow-md drop-shadow-md flex items-center justify-center rounded-full z-50`}
            onClick={toggleMenu}
          >
            <HiChevronDown
              className={`${openMenu && 'rotate-[180deg]'} transition-all duration-350`}
            />
          </button>
          <div
            className={`fixed left-0 ${
              openMenu ? 'top-[96px]' : 'top-[-500px] lg:top-0'
            } lg:relative w-full min-h-[40px] bg-themeBlue-500 flex items-center justify-center lg:justify-end transition-all duration-500 px-8 shadow-md lg:shadow-none drop-shadow-md lg:drop-shadow-none border-t-[1px] lg:border-t-0 border-t-themePurple-900/20 lg:border-t-transparent z-10`}
          >
            <ul className="flex flex-col lg:flex-row items-center justify-center lg:justify-end gap-2 py-5 lg:py-3">
              {Statics.NavigationLinks.map(({ id, label, href }) => (
                <a
                  key={id}
                  className="group min-w-[150px] min-h-[40px] bg-white/80 hover:bg-themePurple-900 flex flex-col items-center justify-center transition-all duration-350 rounded-[50px] shadow-md drop-shadow-md px-3 py-2"
                  href={href}
                >
                  <p className="text-xl lg:text-lg text-themeDarkBlue-900 group-hover:text-white font-urbanist font-semibold uppercase transition-all duration-350">
                    {t(label)}
                  </p>
                </a>
              ))}
            </ul>
          </div>
        </nav>
      </motion.header>
      <main className="w-full h-[100vh]">
        <Outlet />
      </main>
    </div>
  );
};

const Layouts = {
  Public
};

export default Layouts;
